import React, { useState } from "react";
import {
  Box,
  Typography,
  TextField,
  CircularProgress,
  Divider,
  Button,
} from "@mui/material";
import {
  DataGrid,
  GridToolbar,
  gridPageCountSelector,
  gridPageSelector,
  useGridApiContext,
  useGridSelector,
} from "@mui/x-data-grid";
import AuditLogsApi from "../api/AuditLogsApi";
import MuiPagination from "@mui/material/Pagination";

const CustomPagination = ({ page, onPageChange, className }) => {
  const apiRef = useGridApiContext();
  const pageCount = useGridSelector(apiRef, gridPageCountSelector);

  return (
    <MuiPagination
      color="primary"
      className={className}
      count={pageCount}
      page={page + 1}
      onChange={(event, newPage) => onPageChange(event, newPage - 1)}
    />
  );
};

const CustomFooter = ({ totalCount, pageSize, currentPage, onPageChange }) => {
  const from = currentPage * pageSize + 1;
  const to = Math.min(totalCount, (currentPage + 1) * pageSize);

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        padding: "8px 16px",
        backgroundColor: "#f9f9f9",
        borderTop: "1px solid #ddd",
      }}
    >
      <Typography variant="body2">
        Showing {from} to {to} of {totalCount} entries
      </Typography>
      <CustomPagination page={currentPage} onPageChange={onPageChange} />
    </Box>
  );
};

const ViewAuditLogs = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedSuggestion, setSelectedSuggestion] = useState(null);
  const [auditLogs, setAuditLogs] = useState([]);
  const [loading, setLoading] = useState(false);

  const [pageSize, setPageSize] = useState(42);
  const [currentPage, setCurrentPage] = useState(0);

  const handleSearchChange = async (e) => {
    const query = e.target.value.toUpperCase();
    setSearchQuery(query);

    if (auditLogs.length >= 1) {
      setAuditLogs([]);
      setSuggestions([]);
      setSelectedSuggestion(null);
    }

    if (query.length >= 1) {
      try {
        const response = await AuditLogsApi.fetchSuggestions(query);
        setSuggestions(response.data || []);
        setShowSuggestions(true);
      } catch (error) {
        console.error("Error fetching suggestions:", error);
      }
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
    }
  };

  const handleSuggestionClick = (suggestion) => {
    setSearchQuery(suggestion.display_text);
    setSelectedSuggestion(suggestion);
    setShowSuggestions(false);
  };

  const handleViewLogClick = async () => {
    if (!selectedSuggestion) return;

    setLoading(true);
    try {
      const response = await AuditLogsApi.fetchLogsByStyleColor(
        selectedSuggestion.value
      );
      setAuditLogs(response.data || []);
    } catch (error) {
      console.error("Error fetching audit logs:", error);
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (event, newPage) => {
    setCurrentPage(newPage);
  };

  const columns = [
    { field: "store", headerName: "Store", flex: 1, sortable: false },
    { field: "updated_at", headerName: "Updated Date", flex: 1, sortable: false },
    { field: "updated_by", headerName: "Updated By", flex: 1, sortable: false },
    {
      field: "action",
      headerName: "Action",
      flex: 1,
      headerAlign: "center",
      align: "center", sortable: false,
    },
    {
      field: "is_checked",
      headerName: "Is Checked?",
      flex: 1,
      headerAlign: "center",
      align: "center", sortable: false,
    },
    {
      field: "is_imported",
      headerName: "Is from Import?",
      headerAlign: "center",
      flex: 1,
      align: "center", sortable: false,
    },
  ];

  const rows = auditLogs.map((log, index) => ({
    id: index,
    store: log.store,
    updated_at: log.updated_at,
    updated_by: log.updated_by,
    action: log.action,
    is_checked: log.is_checked,
    is_imported: log.is_imported || "N",
  }));

  return (
    <Box>
      <Typography variant="h6" sx={{ marginBottom: 1 }}>
        View Audit Logs
      </Typography>

      <Divider sx={{ marginY: 2 }} />

      <Box sx={{ position: "relative", marginBottom: 2 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "left", // Center-align the content
            gap: 2, // Add consistent spacing between elements
            marginBottom: 2,
          }}
        >
          <Typography sx={{ top: 2, whiteSpace: "nowrap", fontSize: "bold" }}>
            Product Description<span style={{ color: "red" }}>*</span>
          </Typography>
          <TextField
            variant="outlined"
            size="small"
            placeholder="Search by Style code, Style name"
            value={searchQuery}
            onChange={handleSearchChange}
            // sx={{ flexGrow: 1, marginLeft: 20, marginRight: 4 }}
            sx={{ width: "60%", marginLeft: 10, marginRight: 2 }}
          />
          <Button
            variant="contained"
            color="primary"
            onClick={handleViewLogClick}
            disabled={!selectedSuggestion}
          >
            View Logs
          </Button>
        </Box>

        <Divider sx={{ marginY: 2 }} />

        {showSuggestions && (
          <Box
            sx={{
              position: "absolute",
              top: "100%",
              left: 10,
              right: 0,
              background: "white",
              border: "1px solid #ccc",
              borderRadius: "4px",
              zIndex: 2,
              maxHeight: "calc(100vh - 210px)", // Adjust to leave some padding at the bottom
              overflowY: "auto",
            }}
          >
            {suggestions.map((suggestion, index) => (
              <Box
                key={index}
                sx={{
                  padding: "8px",
                  cursor: "pointer",
                  "&:hover": { background: "#f0f0f0" },
                  backgroundColor: index % 2 === 0 ? "#f9f9f8" : "#ffffff",
                }}
                onClick={() => handleSuggestionClick(suggestion)}
              >
                {suggestion.display_text}
              </Box>
            ))}
          </Box>
        )}
      </Box>
      {loading ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "62vh",
          }}
        >
          <CircularProgress />
        </Box>
      ) : auditLogs.length > 0 ? (
        <Box>
          <Box sx={{ height: 700, width: "100%" }}>
            <DataGrid
              rows={rows}
              columns={columns}
              columnHeaderHeight={32}
              rowHeight={22}
              autoPageSize={true}
              page={currentPage}
              paginationMode="client"
              onPageChange={handlePageChange}
              disableColumnFilter
              disableColumnSelector
              disableDensitySelector
              disableColumnMenu
              getRowClassName={(params) =>
                params.indexRelativeToCurrentPage % 2 === 0
                  ? "table-row-even"
                  : "table-row-odd"
              }
              components={{
                Toolbar: GridToolbar,
                Pagination: () => (
                  <CustomFooter
                    totalCount={rows.length}
                    pageSize={pageSize}
                    currentPage={currentPage}
                    onPageChange={handlePageChange}
                  />
                ),
              }}
              sx={{
                "& .MuiDataGrid-columnHeaders": {
                  backgroundColor: "#f7f7f7",
                  fontWeight: "bold",
                },
                "& .MuiDataGrid-columnHeaderTitle": {
                  fontWeight: "bold",
                },
              }}
              slotProps={{
                toolbar: {
                  showQuickFilter: true,
                },
              }}
            />
          </Box>
          <Typography
            variant="body2"
            sx={{
              marginTop: 1,
              textAlign: "left",
              fontSize: "14px",
              color: "gray",
            }}
          >
            Showing 1 to {Math.min(rows.length)} of {rows.length} entries
          </Typography>
        </Box>
      ) : (
        <Typography variant="body1" color="textSecondary" align="center">
          Please search for logs in the above search bar.
        </Typography>
      )}
    </Box>
  );
};

export default ViewAuditLogs;
